import type { Ref } from "react";

import {
  Box,
  Button,
  CodeSnippet,
  Column,
  ExternalLinkIcon,
  ObjectIcon,
  Row,
  Text,
} from "@hightouch/htui";

import { MapperPaneFooter } from "src/formkit/components/mapper-v2/mapper-list";
import { usePartner } from "src/partner/context";
import { Link } from "src/router";

const OBJECT_EXAMPLE = `{
  "street": row.address_line_1,
  "city":   row.city,
  "zip":    "94104"
}`;

type ObjectPaneProps = {
  onCreate: () => void;
  /** The create button, which takes focus when the popover opens. */
  buttonRef: Ref<HTMLButtonElement>;
};

/** Explains nested objects before the mapping turns into one. */
export function ObjectPane({ onCreate, buttonRef }: ObjectPaneProps) {
  const { isPartner } = usePartner();

  return (
    <>
      <Column flex={1} gap={6} minHeight={0} overflowY="auto" p={4}>
        <Column gap={2}>
          <Row alignItems="center" gap={2}>
            <Box as={ObjectIcon} boxSize={6} color="icon.secondary" />
            <Text fontWeight="semibold" size="lg">
              Create an object
            </Text>
          </Row>
          <Text color="text.secondary">
            Send a nested object to the destination. After you create it, rows
            open below this one where you map each key to a column, static
            value, variable, or template.
          </Text>
        </Column>
        <Column gap={2}>
          <Text fontWeight="medium">Example</Text>
          <CodeSnippet code={OBJECT_EXAMPLE} hasCopyButton={false} />
          <Text color="text.tertiary" size="sm">
            Objects can contain objects and arrays nested up to two levels deep.
            {/* Partner portals embed Hightouch, so they don't link to its docs. */}
            {!isPartner && (
              <>
                {" "}
                <Link
                  fontSize="sm"
                  href={`${import.meta.env.VITE_DOCS_URL}/syncs/mapping-data#create-an-object`}
                  isExternal
                  lineHeight="4"
                >
                  Learn more about objects
                  <Box as={ExternalLinkIcon} boxSize={3} ml={1} />
                </Link>
              </>
            )}
          </Text>
        </Column>
      </Column>
      <MapperPaneFooter>
        <Button ref={buttonRef} variant="primary" onClick={onCreate}>
          Create object
        </Button>
      </MapperPaneFooter>
    </>
  );
}
